import React, { useState } from 'react';
import { generateMagicLink } from '@/services/api';

interface DevToolsProps {
  onTokenGenerated: (token: string) => void;
}

/**
 * Development tools component for local testing
 * Only shown in development environment
 */
export const DevTools: React.FC<DevToolsProps> = ({ onTokenGenerated }) => {
  const [customerId, setCustomerId] = useState('test-customer-123');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only show in development
  if (import.meta.env.VITE_ENVIRONMENT !== 'development') {
    return null;
  }

  const handleGenerateToken = async () => {
    if (!customerId.trim()) {
      setError('Customer ID is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await generateMagicLink(customerId);
      
      // Extract token from the URL
      const urlParams = new URLSearchParams(response.uploadUrl.split('?')[1]);
      const token = urlParams.get('token');
      
      if (token) {
        onTokenGenerated(token);
        setError(null);
      } else {
        setError('No token found in response');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate magic link');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 bg-yellow-100 border border-yellow-400 rounded-lg p-4 shadow-lg max-w-sm">
      <div className="flex items-center mb-2">
        <span className="text-yellow-800 font-medium">🛠️ Dev Tools</span>
      </div>
      
      <div className="space-y-3">
        <div>
          <label htmlFor="customer-id" className="block text-sm font-medium text-gray-700 mb-1">
            Customer ID
          </label>
          <input
            id="customer-id"
            type="text"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            className="w-full px-3 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter customer ID"
          />
        </div>
        
        <button
          onClick={handleGenerateToken}
          disabled={isLoading}
          className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white text-sm font-medium py-2 px-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {isLoading ? 'Generating...' : 'Generate Test Token'}
        </button>
        
        {error && (
          <div className="text-red-600 text-xs bg-red-50 p-2 rounded">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default DevTools;